/**
 * PageHeader Component
 *
 * Unified page header with icon, title, description, and actions
 */

import React from "react";
import PropTypes from "prop-types";
import { FaSync, FaExpand, FaCompress } from "react-icons/fa";
import { useFullscreen } from "../../contexts";
import "./PageHeader.scss";

const PageHeader = ({
  icon: Icon,
  iconColor,
  title,
  description,
  onRefresh,
  loading = false,
  actions = [],
  className = "",
  showFullscreenToggle = false,
  belowActions = null,
  onHomeClick,
  homeTooltip = "Back to default view",
}) => {
  const { isFullscreen, toggleFullscreen } = useFullscreen();

  return (
    <div className={`page-header ${className}`}>
      <div
        className={`header-content ${onHomeClick ? "header-content--clickable" : ""}`}
        {...(onHomeClick ? {
          onClick: onHomeClick,
          onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onHomeClick(e); } },
          role: "button",
          tabIndex: 0,
          title: homeTooltip,
        } : {})}
      >
        {Icon && (
          <div className="header-icon" style={{ backgroundColor: iconColor }}>
            <Icon />
          </div>
        )}
        <div className="header-text">
          <h1>{title}</h1>
          {description && <p>{description}</p>}
        </div>
      </div>

      {(onRefresh || (React.isValidElement(actions) ? true : actions.length > 0) || showFullscreenToggle || belowActions) && (
        <div className="header-actions-column">
          <div className="header-actions">
            {/* `actions` is either an array of button configs (default) or a
                ready-made node (e.g. a SegmentedControl) rendered as-is. */}
            {React.isValidElement(actions)
              ? actions
              : actions.map((action, index) => (
              <button
                key={index}
                className={`action-btn ${action.variant || "default"}`}
                onClick={action.onClick}
                disabled={action.disabled || loading}
                title={action.tooltip}
              >
                {action.icon && React.createElement(action.icon)}
                {action.label && <span>{action.label}</span>}
              </button>
            ))}

            {onRefresh && (
              <button
                className="refresh-btn"
                onClick={onRefresh}
                disabled={loading}
                title="Refresh data"
              >
                <FaSync className={loading ? "spinning" : ""} />
                <span>Refresh</span>
              </button>
            )}

            {showFullscreenToggle && (
              <button
                className="fullscreen-btn"
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? <FaCompress /> : <FaExpand />}
              </button>
            )}
          </div>

          {belowActions && <div className="header-below-actions">{belowActions}</div>}
        </div>
      )}
    </div>
  );
};

PageHeader.propTypes = {
  icon: PropTypes.elementType, // React component (e.g., FaExchangeAlt)
  iconColor: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  onRefresh: PropTypes.func,
  loading: PropTypes.bool,
  actions: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        icon: PropTypes.elementType,
        onClick: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
        variant: PropTypes.oneOf(["default", "primary", "danger"]),
        tooltip: PropTypes.string,
      })
    ),
    PropTypes.node,
  ]),
  className: PropTypes.string,
  showFullscreenToggle: PropTypes.bool,
  belowActions: PropTypes.node,
  onHomeClick: PropTypes.func,
  homeTooltip: PropTypes.string,
};

export default PageHeader;
